import React, { useState, useEffect } from 'react';
import { openPdfInNewWindow } from '../utils/pdfHandler';
import '../styles/ExhibitsPage.css';

const ExhibitsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [exhibits, setExhibits] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedExhibit, setSelectedExhibit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExhibits = async () => {
      try {
        // Load exhibits data
        const exhibitsData = require('../data/exhibits.json');
        
        // Extract unique categories for the filter dropdown
        const uniqueCategories = ['All'];
        exhibitsData.forEach(exhibit => {
          if (exhibit.category && !uniqueCategories.includes(exhibit.category)) {
            uniqueCategories.push(exhibit.category);
          }
        });
        
        setCategories(uniqueCategories);
        setExhibits(exhibitsData);
        
        // Debug information
        console.log('Total Exhibits:', exhibitsData.length);
        console.log('Available Categories:', uniqueCategories);
        
        // Select the first exhibit by default
        if (exhibitsData.length > 0) {
          setSelectedExhibit(exhibitsData[0]);
        }
      } catch (error) {
        console.error('Error loading exhibits data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadExhibits();
  }, []);

  useEffect(() => {
    if (exhibits.length === 0 && !loading) {
      console.warn('No exhibits loaded, using fallback data');
      
      // Hardcoded fallback data if nothing else works
      const fallbackExhibits = [
        {
          id: "D1",
          title: "Hair Follicle Test Results",
          description: "Independent hair follicle drug testing showing no substances for past 90 days",
          filePath: "/evidence/exhibits/20230630_EXHIBIT-D1_Hair-Follicle-Test-Results.pdf"
        },   
        {
          id: "C1B",
          title: "Police Confirmation of Missing Evidence",
          category: "Police",
          description: "Call with Sergeant Brockoff confirms city was hacked, resulting in loss of dashcam footage.",
          filePath: "/evidence/documents/20240503_EXHIBIT-C1B_Shenandoah-Police-Call-Transcript.pdf"
        }
        // Add more fallback exhibits as needed
      ];
      
      setExhibits(fallbackExhibits);
      setSelectedExhibit(fallbackExhibits[0]);
    }
  }, [exhibits, loading]);

  // Filter exhibits based on search term and category
  const filteredExhibits = exhibits.filter((exhibit) => {
    const matchesSearch = searchTerm === '' || 
      (exhibit.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
       exhibit.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = 
      categoryFilter === 'All' || 
      exhibit.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Debug filtered results
  console.log('Filtered Exhibits:', filteredExhibits.length);

  if (loading) {
    return <div className="container mx-auto py-8 px-4 text-center">Loading exhibits...</div>;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Case Exhibits</h1>
        <p className="text-gray-600">
          Browse all exhibits related to Mayo v. Montgomery County. Use the search to find specific documents.
        </p>
      </div>

      <div className="mb-6 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
        <div className="flex-grow">
          <input
            type="text"
            placeholder="Search by title or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-4 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="w-48">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="pl-4 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredExhibits.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No exhibits found matching your criteria.</p>
        </div>
      ) : (
        <div className="exhibits-layout">
          <div className="w-full md:w-1/2 lg:w-2/5 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#1e2d44] text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Category</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExhibits.map((exhibit) => (
                  <tr 
                    key={exhibit.id} 
                    className={`cursor-pointer hover:bg-gray-50 ${selectedExhibit?.id === exhibit.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedExhibit(exhibit)}
                  >
                    <td className="px-6 py-3 whitespace-nowrap text-sm">{exhibit.id}</td>
                    <td className="px-6 py-3 text-sm">{exhibit.title}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm">{exhibit.category || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="w-full md:w-1/2 lg:w-3/5 mt-6 md:mt-0 md:pl-6">
            {selectedExhibit ? (
              <div className="bg-white rounded-lg shadow-md">
                <div className="mb-4 pb-4 p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedExhibit.title}</h2>
                  {selectedExhibit.category && (
                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                      {selectedExhibit.category}
                    </span>
                  )}
                </div>
                
                <div className="px-6 mb-4">
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-gray-700">{selectedExhibit.description}</p>
                </div>
                
                {selectedExhibit.eventDetails && (
                  <div className="px-6 mb-4">
                    <h3 className="text-lg font-semibold mb-2">Event Details</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p><span className="font-medium">Date:</span> {selectedExhibit.eventDetails.date}</p>
                      <p><span className="font-medium">Time:</span> {selectedExhibit.eventDetails.time}</p>
                      <p><span className="font-medium">Type:</span> {selectedExhibit.eventDetails.type}</p>
                    </div>
                  </div>
                )}
                
                {selectedExhibit.relatedExhibits && selectedExhibit.relatedExhibits.length > 0 && (
                  <div className="px-6 mb-4">
                    <h3 className="text-lg font-semibold mb-2">Related Exhibits</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedExhibit.relatedExhibits.map(id => {
                        const related = exhibits.find(e => e.id === id);
                        return related ? (
                          <button 
                            key={id}
                            onClick={() => setSelectedExhibit(related)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-1 px-3 rounded text-sm"
                          >
                            {related.id}: {related.title.length > 20 ? related.title.substring(0, 20) + '...' : related.title}
                          </button>
                        ) : (
                          <span key={id} className="bg-gray-100 text-gray-400 py-1 px-3 rounded text-sm">
                            {id} (Not found)
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="px-6 mb-4">
                  <h3 className="text-lg font-semibold mb-2">File Information</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><span className="font-medium">File Type:</span> {selectedExhibit.fileType || 'PDF'}</p>
                    {selectedExhibit.fileSize && (
                      <p><span className="font-medium">File Size:</span> {selectedExhibit.fileSize}</p>
                    )}
                    <p><span className="font-medium">Path:</span> {selectedExhibit.filePath}</p>
                  </div>
                </div>
                
                <div className="flex space-x-4 p-6 bg-gray-50 rounded-b-lg">
                  <button
                    onClick={() => openPdfInNewWindow(selectedExhibit.filePath)}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    View Document
                  </button>
                  
                  {selectedExhibit.audioPath && (
                    <button
                      onClick={() => window.open(selectedExhibit.audioPath, '_blank')}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071a1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243a1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828a1 1 0 010-1.415z" clipRule="evenodd" />
                      </svg>
                      Play Audio
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-state bg-white rounded-lg shadow-md p-10 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-xl font-medium text-gray-900 mt-4">No Exhibit Selected</h3>
                <p className="text-gray-500 mt-2">Select an exhibit from the list to view details.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExhibitsPage;